import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useState } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";

import { useTakeActionModal } from "./use-action-modal";
import { Button } from "@/components/ui/button/button";

import { takeActionOnAttendanceRequest } from "@/config/mutation/admin";
import { Textarea } from "@/components/ui/textarea/textarea";

export const TakeActionOnAttendanceRequestModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, attRequestId, otherInfo, setOtherInfo } =
    useTakeActionModal();

  const [formData, setFormData] = useState({
    status: "",
    rejectReason: "",
    attendenceStatus: "",
  });

  const { mutate, isPending } = useMutation({
    mutationFn: takeActionOnAttendanceRequest(key),
  });

  const submitAction = async () => {
    if (!formData.status || formData.status === undefined) {
      return toast.error("Please select an action first.");
    }

    const realData = {
      status: formData?.status,
      rejectReason:
        formData?.status === "Rejected" ? formData?.rejectReason : undefined,
      attendanceStatus:
        formData?.status === "Approved" ? formData?.attendenceStatus : "",
      attReqId: attRequestId,
    };

    mutate(realData, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["attendance-requests"],
        });

        toast.success("Action Taken.");
        onOpenChange(false);
        setFormData({
          status: "",
          rejectReason: "",
          attendenceStatus: "",
        });
        setOtherInfo(null);
      },

      onError: (error) => {
        console.log(
          "ERROR WHILE CREATING TAKING ACTION ON ATTENDANCE REQUEST",
          error
        );
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="tracking-normal">
              Take action on attendance request
            </DialogTitle>
          </DialogHeader>

          {otherInfo && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                <p>Employee Name : </p>
                <p className="font-semibold">{otherInfo?.employeeName}</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <p>Notes : </p>
                <p className="font-normal tracking-normal">
                  {otherInfo?.notes}
                </p>
              </div>
            </div>
          )}

          <div className="mt-5">
            <Select
              value={formData.status}
              onValueChange={(value) => {
                setFormData((prev) => {
                  return {
                    ...prev,
                    status: value,
                  };
                });
              }}
              disabled={isPending}
            >
              <span>Choose Action</span>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Action..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            {formData?.status === "Approved" && (
              <>
                <div className="my-5">
                  <Select
                    value={formData?.attendenceStatus}
                    onValueChange={(value) => {
                      setFormData((prev) => {
                        return {
                          ...prev,
                          attendenceStatus: value,
                        };
                      });
                    }}
                    disabled={isPending}
                  >
                    <span>Choose Attendance Status</span>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Attendance Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Present">Present</SelectItem>
                      <SelectItem value="Absent">Absent</SelectItem>
                      <SelectItem value="Half-day">Half-day</SelectItem>
                      <SelectItem value="Paid Leave">Paid Leave</SelectItem>
                      <SelectItem value="On Leave">On Leave</SelectItem>
                      <SelectItem value="Holiday">Holiday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {formData?.status === "Rejected" && (
              <>
                <div className="mt-5">
                  <span>Reject Reason</span>
                  <Textarea
                    name="rejectReason"
                    placeholder="Reject Reason..."
                    value={formData?.rejectReason}
                    onChange={(e) => {
                      setFormData((prev) => {
                        return {
                          ...prev,
                          [e.target.name]: e.target.value,
                        };
                      });
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end">
            <Button onClick={submitAction} disabled={isPending}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
